import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Button, LinearProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup,
  TextField, alpha,
} from '@mui/material';
import { Gavel, AutoAwesome, PlayArrow } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { admissionCycleService } from '../../services/admissions/admissionCycleService';
import { admissionApplicationService } from '../../services/admissions/admissionApplicationService';
import {
  AdmissionCycle, AdmissionApplication, ApplicationStatus,
} from '../../types/admissions';
import StatusChip, { ToneChip, ChipTone } from '../common/StatusChip';
import EmptyState from '../common/EmptyState';
import TableRowSkeletons from '../common/TableRowSkeletons';
import SeatSnapshotStrip from './SeatSnapshotStrip';
import CandidateDrawer from './CandidateDrawer';
import ApplicationActionsMenu from './ApplicationActionsMenu';
import {
  TabToolbar, TableCard, RankBadge, tableHeadCellSx, clickableRowSx,
  ClassSearchFilters, ApplicantCell, RowActionsCell, SeatCutLineRow, NoSearchMatchRow,
  applicantMatches, useCycleClassOptions, numCellSx,
} from './tabLayout';
import { useDebounce } from '../../hooks/useDebounce';
import { fmtClassName, fmtNumber } from '../../utils/pdf/format';
import ConfirmationDialog, { ConfirmFacts } from '../common/ConfirmationDialog';
import { assessmentPlan, AssessmentPlan, pathwayFor, pendingOverride, testOutcome } from '../../utils/admissionAssessment';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useUpcomingEmails } from '../email/useUpcomingEmails';
import UpcomingEmailNotice from '../email/UpcomingEmailNotice';
import { decisionEmailRequests } from './decisionEmails';

/**
 * DecisionsTab — the batch "offer / waitlist / reject" console on the merit
 * list. Proposal ("Propose"), in merit order:
 *   - the next applicants up to the seats still left        → Offer
 *   - the next ones up to the waitlist space still left     → Waitlist
 *   - below the test pass mark                             → Reject
 *   - everyone else                                        → Skip
 * Rows that can't be decided here are locked with the reason (missing
 * marks, already decided, awaiting an approval). Applying runs each row
 * through the server's rules in merit order; the results dialog shows
 * anything that wasn't applied and why.
 */

type DecisionChoice = 'Offered' | 'Waitlisted' | 'Rejected' | 'Skip';

/** Statuses waiting for a decision under this cycle's stages. */
const decidableFrom = (plan: AssessmentPlan): ApplicationStatus[] =>
  plan.hasAssessment ? ['Assessed'] : ['UnderReview', 'DocsRequired'];

// Still working through review / assessment — a locked row in one of these
// is "not ready"; any other locked row has already been decided.
const PRE_DECISION: ApplicationStatus[] = [
  'Draft', 'Submitted', 'AppFeePending', 'UnderReview', 'DocsRequired', 'AssessmentScheduled', 'Absent',
];

const CHOICES: DecisionChoice[] = ['Offered', 'Waitlisted', 'Rejected', 'Skip'];

const CHOICE_META: Record<DecisionChoice, { label: string; tone: ChipTone }> = {
  Offered:    { label: 'Offer',    tone: 'success' },
  Waitlisted: { label: 'Waitlist', tone: 'info'    },
  Rejected:   { label: 'Reject',   tone: 'error'   },
  Skip:       { label: 'Skip',     tone: 'neutral' },
};

const DECISION_COLUMNS = 6;

const OUTCOME_META: Record<BulkOutcome[number]['outcome'], { label: string; tone: ChipTone }> = {
  transitioned: { label: 'Done', tone: 'success' },
  noop: { label: 'No change', tone: 'neutral' },
  failed: { label: 'Not applied', tone: 'error' },
};

interface Props { cycle: AdmissionCycle; }

interface RowState {
  app: AdmissionApplication;
  rank: number | null;
  choice: DecisionChoice;
  /** Why the row can't be decided here; null when it can. */
  lockReason: string | null;
  /** Below the test pass mark — can only be rejected here. */
  failed: boolean;
}

type BulkOutcome = Array<{ applicationId: string; outcome: 'transitioned' | 'noop' | 'failed'; message?: string }>;

const DecisionsTab: React.FC<Props> = ({ cycle }) => {
  const { enqueueSnackbar } = useSnackbar();
  // Deciding needs `admissions_decisions`; without it the board is read-only.
  const { canWrite } = useModulePermissions();
  const canDecide = canWrite('admissions_decisions');

  const [classId, setClassId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<RowState[]>([]);
  const [seatSnapshot, setSeatSnapshot] = useState<any>(null);
  const [sharedReason, setSharedReason] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  // Row ⋮ menu — one shared menu, anchored to whichever row opened it.
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; app: AdmissionApplication } | null>(null);
  const [applying, setApplying] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [results, setResults] = useState<BulkOutcome>([]);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 250);

  // Client-side search filter — the server returns up to 1000 ranked rows at
  // once, so narrowing is instant. Choices stay bound to the underlying
  // `rows` so filtering doesn't lose un-applied decisions.
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => applicantMatches(r.app, q));
  }, [rows, search]);

  // Resolve class options from the cycle shell.
  const classOptions = useCycleClassOptions(cycle);
  const plan = useMemo(() => assessmentPlan(pathwayFor(cycle, classId)), [cycle, classId]);

  // Per-class waitlist size from the seat matrix.
  const waitlistSize = useMemo(() => {
    if (!classId) return 0;
    const entry = cycle.seatMatrix?.find(e => e.classId === classId);
    return entry?.waitlistSize ?? 0;
  }, [classId, cycle.seatMatrix]);

  useEffect(() => {
    if (!classId && classOptions.length > 0) setClassId(classOptions[0]._id);
  }, [classId, classOptions]);

  /** Default choices in merit order, within the seats and waitlist space left. */
  const propose = useCallback((list: RowState[], seatsLeft: number, waitlistLeft: number): RowState[] => {
    let offers = seatsLeft;
    let waits = waitlistLeft;
    return list.map(r => {
      if (r.lockReason) return { ...r, choice: 'Skip' };
      if (r.failed) return { ...r, choice: 'Rejected' };
      if (offers > 0) { offers -= 1; return { ...r, choice: 'Offered' }; }
      if (waits > 0) { waits -= 1; return { ...r, choice: 'Waitlisted' }; }
      return { ...r, choice: 'Skip' };
    });
  }, []);

  const load = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      // Pull all ranked rows for this class — the merit list sort is already
      // (rank, score). Decisions always operate on the full set, not a page.
      const res = await admissionCycleService.meritList(cycle._id, {
        classId,
        limit: 1000,
        page: 1,
      });
      const snap = res.seatSnapshot;
      setSeatSnapshot(snap);
      const decidable = decidableFrom(plan);
      const base: RowState[] = res.items.map(app => {
        const outcome = testOutcome(app, plan);
        const lockReason = pendingOverride(app) ? 'Awaiting approval'
          : !decidable.includes(app.status)
            ? (PRE_DECISION.includes(app.status) ? 'Not ready for a decision' : 'Already decided')
            : app.meritRank == null && app.meritGaps?.length ? `Missing: ${app.meritGaps.join(', ')}`
              : null;
        return { app, rank: app.meritRank ?? null, choice: 'Skip', lockReason, failed: !!outcome && !outcome.passed };
      });
      const waitlisted = res.items.filter(a => a.status === 'Waitlisted').length;
      const next = propose(base, snap?.remaining ?? 0, Math.max(0, waitlistSize - waitlisted));
      setRows(next);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load decision board', { variant: 'error' });
      setRows([]);
      setSeatSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [cycle._id, classId, waitlistSize, plan, propose, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const setChoice = (idx: number, choice: DecisionChoice) => {
    setRows(prev => prev.map((r, i) => (i === idx && !r.lockReason ? { ...r, choice } : r)));
  };

  const resetProposed = () => {
    if (!seatSnapshot) { load(); return; }
    const waitlisted = rows.filter(r => r.app.status === 'Waitlisted').length;
    setRows(prev => propose(prev, seatSnapshot.remaining ?? 0, Math.max(0, waitlistSize - waitlisted)));
  };

  // Summary counters.
  const counts = useMemo(() => {
    const c = { Offered: 0, Waitlisted: 0, Rejected: 0, Skip: 0, locked: 0 };
    for (const r of rows) {
      if (r.lockReason) c.locked += 1;
      else c[r.choice] += 1;
    }
    return c;
  }, [rows]);
  const toApply = counts.Offered + counts.Waitlisted + counts.Rejected;

  const [confirmOpen, setConfirmOpen] = useState(false);
  // Offers and rejections may email families — counted when the confirm opens.
  const emailRequests = useMemo(
    () => (confirmOpen ? decisionEmailRequests(rows.filter(r => !r.lockReason).map(r => ({ to: r.choice as ApplicationStatus, id: r.app._id }))) : []),
    [confirmOpen, rows],
  );
  const upcoming = useUpcomingEmails(emailRequests);
  const className = fmtClassName(classOptions.find(c => c._id === classId)?.name);

  const applyDecisions = async () => {
    setConfirmOpen(false);
    const decisions = rows
      .filter(r => !r.lockReason && r.choice !== 'Skip')
      .map(r => ({
        applicationId: r.app._id,
        to: r.choice as ApplicationStatus,
        reason: sharedReason.trim() || `Batch decision (${CHOICE_META[r.choice].label.toLowerCase()})`,
      }));

    if (decisions.length === 0) {
      enqueueSnackbar('Nothing to apply — every row is Skip or locked', { variant: 'info' });
      return;
    }

    setApplying(true);
    try {
      const out = await admissionApplicationService.bulkDecide(decisions);
      setResults(out);
      setResultsOpen(true);
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to apply decisions', { variant: 'error' });
    } finally {
      setApplying(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  if (classOptions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" variant="outlined">
          No classes are configured on this cycle. Add classes under <b>Settings → Seat matrix</b>.
        </Alert>
      </Box>
    );
  }

  const hardCap: number | undefined = seatSnapshot?.hardCap;

  return (
    <Box>
      <TabToolbar
        summary={rows.length > 0 ? `${rows.length} on the list` : undefined}
        onRefresh={load}
        refreshing={loading}
        actions={canDecide && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoAwesome fontSize="small" />}
            onClick={resetProposed}
            disabled={loading || rows.length === 0}
            sx={{ textTransform: 'none' }}
          >
            Propose
          </Button>
        )}
      >
        <ClassSearchFilters
          classOptions={classOptions}
          classId={classId}
          onClassChange={setClassId}
          search={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Find by name or GR…"
        />
      </TabToolbar>

      {/* ── Seat snapshot ───────────────────────────────────────────────── */}
      {seatSnapshot && <SeatSnapshotStrip snapshot={seatSnapshot} />}

      {/* ── Decision action bar ─────────────────────────────────────────── */}
      {rows.length > 0 && canDecide && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: (t) => alpha(t.palette.text.primary, 0.015) }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
            {(['Offered', 'Waitlisted', 'Rejected'] as DecisionChoice[]).map(k => (
              <ToneChip
                key={k}
                tone={counts[k] > 0 ? CHOICE_META[k].tone : 'neutral'}
                label={`${counts[k]} ${CHOICE_META[k].label}`}
                sx={numCellSx}
              />
            ))}
            {counts.Skip > 0 && <ToneChip tone="neutral" label={`${counts.Skip} Skip`} sx={numCellSx} />}
            {counts.locked > 0 && <ToneChip tone="neutral" label={`${counts.locked} Locked`} sx={numCellSx} />}
            {waitlistSize > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                Waitlist cap {waitlistSize}
              </Typography>
            )}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            {toApply === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {counts.locked === rows.length ? 'Nothing left to decide in this class' : 'Choose Offer, Waitlist or Reject on a row to apply'}
              </Typography>
            )}
            <TextField
              size="small"
              value={sharedReason}
              onChange={(e) => setSharedReason(e.target.value)}
              placeholder="Reason (optional, applied to all)"
              disabled={toApply === 0}
              sx={{ minWidth: 260 }}
            />
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<PlayArrow fontSize="small" />}
              onClick={() => setConfirmOpen(true)}
              disabled={applying || loading || toApply === 0}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {applying ? 'Applying…' : toApply > 0 ? `Apply ${toApply} decision${toApply === 1 ? '' : 's'}` : 'Apply decisions'}
            </Button>
          </Stack>
        </Stack>
      )}

      {/* Refetch progress bar — keeps toolbar + summary stable. */}
      {loading && rows.length > 0 && (
        <LinearProgress sx={{ height: 2 }} />
      )}

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <Box sx={{ px: 2.5, py: 2.5 }}>
        {!loading && rows.length === 0 ? (
          <EmptyState
            icon={<Gavel />}
            title="Nothing to decide yet"
            description={plan.hasAssessment
              ? 'Applicants appear here once they are assessed. Enter marks on the Assessment tab of the applicant drawer.'
              : 'Applicants appear here once they are under review.'}
            dense
          />
        ) : (
          <TableCard>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tableHeadCellSx, width: 64 }}>Rank</TableCell>
                  <TableCell sx={tableHeadCellSx}>Applicant</TableCell>
                  <TableCell align="right" sx={tableHeadCellSx}>Merit</TableCell>
                  <TableCell sx={tableHeadCellSx}>Current</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx, width: 340 }}>Decision</TableCell>
                  <TableCell align="right" sx={{ ...tableHeadCellSx, width: 56 }}>&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && rows.length === 0 ? (
                  <TableRowSkeletons columns={DECISION_COLUMNS} rows={6} />
                ) : visibleRows.length === 0 ? (
                  <NoSearchMatchRow colSpan={DECISION_COLUMNS} search={search} onClear={() => setSearchInput('')} />
                ) : (
                  visibleRows.map((r, vIdx) => {
                    const idx = rows.indexOf(r);
                    const underCap = hardCap != null && r.rank != null && r.rank <= hardCap;
                    const prevRank = vIdx > 0 ? visibleRows[vIdx - 1].rank : null;
                    const prevUnderCap = hardCap != null && prevRank != null && prevRank <= hardCap;
                    return (
                      <React.Fragment key={r.app._id}>
                        {prevUnderCap && !underCap && hardCap != null && (
                          <SeatCutLineRow colSpan={DECISION_COLUMNS} seats={hardCap} />
                        )}
                        <DecisionRow
                          row={r}
                          underCap={underCap}
                          onChoice={(c) => setChoice(idx, c)}
                          readOnly={!canDecide}
                          onOpen={() => setOpenId(r.app._id)}
                          onMenu={(anchor) => setRowMenu({ anchor, app: r.app })}
                        />
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableCard>
        )}
      </Box>

      {/* ── Results dialog ──────────────────────────────────────────────── */}
      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={applyDecisions}
        title={`Apply ${toApply} decision${toApply === 1 ? '' : 's'}${className ? ` in ${className}` : ''}?`}
        message={`${counts.Offered > 0 ? 'Offers start their acceptance deadline and take seats. ' : ''}${counts.Rejected > 0 ? 'Rejected applicants are closed — reopening one later needs a reason. ' : ''}Skipped rows stay as they are.`}
        confirmText={upcoming.sending ? `Apply ${toApply} and send emails` : `Apply ${toApply}`}
        confirmColor="primary"
        confirmDisabled={upcoming.loading}
      >
        <ConfirmFacts items={[
          { label: 'Offer a seat', value: counts.Offered },
          { label: 'Waitlist', value: counts.Waitlisted },
          { label: 'Reject', value: counts.Rejected },
          ...(sharedReason.trim() ? [{ label: 'Reason', value: sharedReason.trim() }] : []),
        ].filter(f => f.value !== 0)} />
        <Box sx={{ mt: 1.5 }}><UpcomingEmailNotice upcoming={upcoming} when="as soon as you apply" /></Box>
      </ConfirmationDialog>
      <Dialog open={resultsOpen} onClose={() => setResultsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle component="div">
          <Typography variant="h6" fontWeight={700}>Decision results</Typography>
          <Typography variant="caption" color="text.secondary">
            {results.filter(r => r.outcome === 'transitioned').length} done ·{' '}
            {results.filter(r => r.outcome === 'noop').length} no change ·{' '}
            {results.filter(r => r.outcome === 'failed').length} not applied
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {results.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No rows applied.</Typography>
          ) : (
            <Stack spacing={1}>
              {results.map((r, i) => {
                const row = rows.find(x => x.app._id === r.applicationId);
                return (
                  <Paper
                    key={`${r.applicationId}-${i}`}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      borderColor: r.outcome === 'failed' ? 'error.main' : 'divider',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {row?.app.name || r.applicationId}
                        </Typography>
                        {r.message && (
                          <Typography variant="caption" color={r.outcome === 'failed' ? 'error.main' : 'text.secondary'}>
                            {r.message}
                          </Typography>
                        )}
                      </Box>
                      <ToneChip tone={OUTCOME_META[r.outcome].tone} label={OUTCOME_META[r.outcome].label} />
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultsOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <ApplicationActionsMenu
        app={rowMenu?.app ?? null}
        cycle={cycle}
        anchorEl={rowMenu?.anchor ?? null}
        onClose={() => setRowMenu(null)}
        onMutated={load}
      />

      {openId && (
        <CandidateDrawer
          applicationId={openId}
          cycle={cycle}
          onClose={() => setOpenId(null)}
          onMutated={load}
        />
      )}
    </Box>
  );
};

// ─── Row ────────────────────────────────────────────────────────────────────
interface DecisionRowProps {
  row: RowState;
  underCap: boolean;
  onChoice: (c: DecisionChoice) => void;
  readOnly: boolean;
  onOpen: () => void;
  onMenu: (anchor: HTMLElement) => void;
}

const DecisionRow: React.FC<DecisionRowProps> = ({ row, underCap, onChoice, readOnly, onOpen, onMenu }) => (
  <TableRow hover onClick={onOpen} sx={clickableRowSx}>
    <TableCell>
      {row.rank != null ? <RankBadge rank={row.rank} inside={underCap} /> : <Typography variant="body2" color="text.disabled">—</Typography>}
    </TableCell>
    <TableCell><ApplicantCell app={row.app} /></TableCell>
    <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700 }}>
      {row.app.computedMeritScore != null ? fmtNumber(row.app.computedMeritScore, 2) : '—'}
    </TableCell>
    <TableCell>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        <StatusChip status={{ kind: 'application', value: row.app.status }} />
        {row.failed && <ToneChip label="Below pass mark" tone="error" />}
      </Box>
    </TableCell>
    {/* The decision control must not open the drawer. */}
    <TableCell onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
      {row.lockReason ? (
        <Typography variant="caption" color="text.secondary">{row.lockReason}</Typography>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            size="small"
            value={row.choice}
            exclusive
            onChange={(_, v) => v && onChoice(v)}
            disabled={readOnly}
            aria-label={`Decision for ${row.app.name}`}
          >
            {CHOICES.map(k => (
              <ToggleButton
                key={k}
                value={k}
                // Below the pass mark: an offer needs an approval from the drawer.
                disabled={row.failed && (k === 'Offered' || k === 'Waitlisted')}
                sx={(t) => {
                  const tone = CHOICE_META[k].tone;
                  const c = tone === 'neutral' ? t.palette.text.secondary : t.palette[tone as 'success' | 'info' | 'error'].main;
                  return {
                    textTransform: 'none', px: 1.25, py: 0.25, fontSize: '0.78rem', fontWeight: 600,
                    color: 'text.secondary', borderColor: 'divider',
                    '&.Mui-selected, &.Mui-selected:hover': { color: c, bgcolor: alpha(c, 0.1) },
                  };
                }}
              >
                {CHOICE_META[k].label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      )}
    </TableCell>
    <RowActionsCell app={row.app} onMenu={onMenu} />
  </TableRow>
);

export default DecisionsTab;
